import type { RefObject } from 'react';
import { Alert, Button } from '@/design-system';
import type { ApplicationView } from '@/lib/intake/api';
import type { Catalogue } from '@/lib/intake/catalogue';
import type { Evaluation } from '@/lib/intake/rules';
import { formatDate } from '@/lib/format';
import { COPY } from './copy';
import { SummaryActions } from './SummaryActions';
import styles from './Journey.module.css';

export interface ConfirmationStepProps {
  headingRef: RefObject<HTMLHeadingElement | null>;
  application: ApplicationView;
  catalogue: Catalogue;
  evaluation: Evaluation;
  onViewAnswers: () => void;
}

/**
 * What an applicant sees once the application has gone: its reference, when it
 * went, what happens next, and what was missing. The answers stay one click away,
 * read-only.
 */
export function ConfirmationStep({ headingRef, ...props }: ConfirmationStepProps) {
  const gaps = props.catalogue.sections
    .map((section, index) => ({ section, number: index + 1, complete: props.evaluation.sections[index]?.complete }))
    .filter((s) => s.complete === false);
  return (
    <section className={styles.card} aria-labelledby="step-heading">
      <h1 id="step-heading" ref={headingRef} tabIndex={-1} className={styles.title}>
        {COPY.sentTitle}
      </h1>
      <dl className={styles.facts}>
        <div>
          <dt>{COPY.sentReference}</dt>
          <dd className={styles.reference}>{props.application.reference}</dd>
        </div>
        {props.application.submittedAt && (
          <div>
            <dt>{COPY.sentOn}</dt>
            <dd>{formatDate(props.application.submittedAt)}</dd>
          </div>
        )}
        <div>
          <dt>{COPY.sentStatus}</dt>
          <dd>{COPY.statusNames[props.application.status]}</dd>
        </div>
      </dl>
      <p className={styles.intro}>{COPY.sentConfirmation}</p>
      {gaps.length > 0 && (
        <Alert tone="info">
          {COPY.sentWithGaps(gaps.length)}
          <ul className={styles.sentGaps}>
            {gaps.map(({ section, number }) => (
              <li key={section.id}>
                {number}. {section.title}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <section aria-labelledby="what-next" className={styles.reviewSection}>
        <h2 id="what-next" className={styles.subtitle}>
          {COPY.whatNext}
        </h2>
        <p className={styles.intro}>{COPY.whatNextText}</p>
      </section>
      <SummaryActions applicationId={props.application.applicationId} />
      <div className={styles.actions}>
        <Button variant="secondary" onClick={props.onViewAnswers}>
          {COPY.viewSent}
        </Button>
      </div>
    </section>
  );
}
